import {addDays, addMonths, format, parseISO} from "date-fns";
import type {Color, TenantCreateRequest} from "../types/tenant-create";
import {defaultFeatureConfig} from "../types/tenant-create";
import type {QuotaChangeCreateRequest, TenantFeatureConfig} from "../types/tenant";
import type {AvailabilityRules} from "../util/default-availability";

export const TERM_OPTIONS_MONTHS = [1, 3, 6, 12] as const;
const GRACE_DAYS = 14;
const BYTES_PER_MEGABYTE = 1024 * 1024;
const MAX_NAME_LENGTH = 256;
const MAX_NOTES_LENGTH = 50000;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// The backend records the SMS quota granted at creation under this note; follow-up changes match it.
const INITIAL_QUOTA_NOTE = "Anfängliches Kontingent";

export type InitialQuotas = {sms: number; storageMegabytes: number; email: number};

export type CreateTenantForm = {
    name: string;
    administratorEmail: string;
    notes: string;
    planId: string;
    maxEmployees: number;
    startDate: string;
    paidUntil: string;
    activeUntil: string;
    /** The term chip that produced the end dates; `null` once a date is edited by hand. */
    termMonths: number | null;
    isFreePlan: boolean;
    amountDue: number;
    amountPaid: number;
    featureConfig: TenantFeatureConfig;
    quotas: InitialQuotas;
};

export type CreateTenantField =
        | "name"
        | "administratorEmail"
        | "notes"
        | "planId"
        | "maxEmployees"
        | "paidUntil"
        | "activeUntil"
        | "amountDue"
        | "amountPaid";

export type CreateTenantErrors = Partial<Record<CreateTenantField, string>>;

const DAY_FORMAT = "yyyy-MM-dd";

/** Paid-until is the start plus the term; access runs 14 days longer. */
export function termEndDates(startDate: string, months: number): {paidUntil: string; activeUntil: string} {
    const paidUntil = addMonths(parseISO(startDate), months);
    return {paidUntil: format(paidUntil, DAY_FORMAT), activeUntil: format(addDays(paidUntil, GRACE_DAYS), DAY_FORMAT)};
}

export function initialCreateTenantForm(now: Date): CreateTenantForm {
    const startDate = format(now, DAY_FORMAT);
    return {
        name: "",
        administratorEmail: "",
        notes: "",
        planId: "",
        maxEmployees: 1,
        startDate,
        ...termEndDates(startDate, 1),
        termMonths: 1,
        isFreePlan: true,
        amountDue: 0,
        amountPaid: 0,
        featureConfig: defaultFeatureConfig(),
        quotas: {sms: 0, storageMegabytes: 0, email: 0},
    };
}

/** Values are i18n keys of the field's message. */
export function validateCreateTenantForm(form: CreateTenantForm): CreateTenantErrors {
    const errors: CreateTenantErrors = {};
    if (!form.name.trim()) errors.name = "CREATE_VALIDATION_NAME_REQUIRED";
    else if (form.name.length > MAX_NAME_LENGTH) errors.name = "PROMOTIONS_VALIDATION_MAX_256";
    if (!form.administratorEmail.trim()) errors.administratorEmail = "CREATE_VALIDATION_ADMIN_EMAIL_REQUIRED";
    else if (!EMAIL_PATTERN.test(form.administratorEmail.trim())) errors.administratorEmail = "CREATE_VALIDATION_INVALID_EMAIL";
    if (form.notes.length > MAX_NOTES_LENGTH) errors.notes = "CREATE_VALIDATION_NOTES_MAX_50000";
    if (!form.planId) errors.planId = "CREATE_VALIDATION_PLAN_REQUIRED";
    if (form.maxEmployees < 1) errors.maxEmployees = "CREATE_VALIDATION_AT_LEAST_1";
    if (!form.paidUntil) errors.paidUntil = "CREATE_VALIDATION_REQUIRED";
    if (!form.activeUntil) errors.activeUntil = "CREATE_VALIDATION_REQUIRED";
    if (!form.isFreePlan && form.amountDue < 0) errors.amountDue = "CREATE_VALIDATION_MIN_ZERO";
    if (!form.isFreePlan && form.amountPaid < 0) errors.amountPaid = "CREATE_VALIDATION_MIN_ZERO";
    return errors;
}

export function buildCreateTenantRequest(
        form: CreateTenantForm,
        generated: {color: Color; availabilityRules: AvailabilityRules},
): TenantCreateRequest {
    const name = form.name.trim();
    return {
        name,
        // Manual creation is Austria-only; the backend has no country input on this path.
        countryAlpha2: "AT",
        administrator: {
            alias: name,
            availabilityRules: generated.availabilityRules,
            canLogin: true,
            color: generated.color,
            emailAddress: form.administratorEmail.trim(),
        },
        activeSubscription: {
            planId: form.planId,
            startDate: new Date(form.startDate).toISOString(),
            paidUntil: form.paidUntil,
            activeUntil: form.activeUntil,
            maxEmployees: form.maxEmployees,
            amountDueInCents: form.isFreePlan ? 0 : Math.round(form.amountDue * 100),
            amountPaidInCents: form.isFreePlan ? 0 : Math.round(form.amountPaid * 100),
        },
        notes: form.notes.trim() || undefined,
        featureConfig: {...form.featureConfig},
        quotas: {
            sms: {max: form.quotas.sms, used: 0},
            storageBytes: {max: form.quotas.storageMegabytes * BYTES_PER_MEGABYTE, used: 0},
            email: {max: form.quotas.email, used: 0},
        },
    };
}

/**
 * Creation grants only the SMS quota from the request; storage and e-mail have to be added as quota
 * changes afterwards, exactly as an operator would through Edit quotas.
 */
export function initialQuotaChanges(quotas: InitialQuotas): QuotaChangeCreateRequest[] {
    const changes: QuotaChangeCreateRequest[] = [];
    if (quotas.storageMegabytes > 0) {
        changes.push({quota: "storageBytes", amount: quotas.storageMegabytes * BYTES_PER_MEGABYTE, note: INITIAL_QUOTA_NOTE});
    }
    if (quotas.email > 0) changes.push({quota: "email", amount: quotas.email, note: INITIAL_QUOTA_NOTE});
    return changes;
}
